import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/empty-state'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { ResidentComposer, type ResidentComposerProps } from './ResidentComposer'

/**
 * The ONE window every external door renders (spec 2026-09-18-05).
 *
 * Spec 2026-09-18-03 lifted the composer out and pointed the QR door and the
 * printed address at it. What it did not lift was the WINDOW around it, so a
 * resident wrote their first message in a cramped box under a page heading and
 * then, one send later, was dropped into a tall thread with a header, a status
 * badge and a reference. Same widget, two entirely different pages, and no way
 * to tell it was the same place. The anonymous path is where that hurts most:
 * with no e-mail to fall back on, the thread IS the whole relationship.
 *
 * So the shell is the component, not a visual approximation of one — step two
 * of `/q/` and `/r/` and the live `/c/` thread render this file, and the send
 * that creates the thread is now only the message list and the header filling
 * in.
 *
 * What a door that has no thread yet must NOT do is invent one: no status badge
 * and no reference, because there is nothing to name. Everything else — the
 * organization line, the title, the message area, the composer pinned at the
 * bottom at the same size with the same four affordances — is identical by
 * construction.
 */
export interface ResidentThreadShellProps {
    /** The organization, in the small line above the title. */
    organizationName: string
    /** The thread's name; the asset's name on a door with no thread yet. */
    title: string
    /** Omitted before the thread exists — there is no status to show. */
    status?: string | null
    assetName?: string
    /** Omitted before the thread exists — there is no reference to quote. */
    reference?: string | null
    /** The "open in console" link, and anything else header-level. */
    headerExtra?: ReactNode
    /** The bubbles. Absent (or empty) renders the empty state below. */
    children?: ReactNode
    /** True while the thread holds no message. */
    empty?: boolean
    /** The line naming what can be sent, shown where the first message will. */
    emptyIntro?: string | null
    /** Rendered between the message area and the composer (the survey). */
    beforeComposer?: ReactNode
    composer: ResidentComposerProps
    /** Overrides the root test id for a door that had one before this shell. */
    pageTestId?: string
}

export function ResidentThreadShell({
    organizationName,
    title,
    status,
    assetName,
    reference,
    headerExtra,
    children,
    empty,
    emptyIntro,
    beforeComposer,
    composer,
    pageTestId,
}: ResidentThreadShellProps) {
    const { t } = useTranslation('conversations')
    const closed = status === 'closed'

    return (
        <div
            className="mx-auto max-w-xl flex flex-col min-h-screen bg-muted/40"
            data-testid={pageTestId ?? 'resident-page'}
            data-thread-shell="true"
        >
            <header
                className="px-4 pt-6 pb-4 md:px-6 space-y-2 border-b bg-background sticky top-0 z-10"
                data-testid="resident-thread-header"
            >
                <p className="text-xs uppercase tracking-wide text-muted-foreground" data-testid="resident-thread-org">
                    {organizationName}
                </p>
                <PageHeader
                    icon={SECTION_ICONS.conversations}
                    title={title || t('queue.no_subject')}
                    wrap
                    data-testid="resident-title"
                    titleAside={
                        status ? (
                            <Badge
                                variant={closed ? 'outline' : 'secondary'}
                                className="text-[10px]"
                                data-testid="resident-status"
                            >
                                {t(`public.status_${status}`, { defaultValue: status })}
                            </Badge>
                        ) : undefined
                    }
                    description={
                        assetName || reference ? (
                            <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                {assetName && (
                                    <span className="inline-flex items-center gap-1" data-testid="resident-thread-asset">
                                        <AppIcon path={SECTION_ICONS.assets} size="xs" />
                                        {assetName}
                                    </span>
                                )}
                                {reference && (
                                    <span className="font-mono">{t('public.reference', { value: reference })}</span>
                                )}
                            </span>
                        ) : undefined
                    }
                />
                {headerExtra}
            </header>

            <main className="flex-1 px-3 md:px-6 py-4 space-y-2" data-testid="resident-messages">
                {empty && (
                    <EmptyState
                        variant="inline"
                        title={t('panel.no_messages')}
                        description={emptyIntro ?? undefined}
                        data-testid="resident-thread-empty"
                    />
                )}
                {children}
            </main>

            {beforeComposer}

            {/* Opaque, so the bubbles scrolling under it do not show through,
                and tinted exactly like the thread above it: the composer sits
                ON the conversation, as it does on WhatsApp, not in a panel. */}
            <footer className="sticky bottom-0 bg-background" data-testid="resident-thread-composer">
                <div className="bg-muted/40 px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:px-4">
                    <ResidentComposer {...composer} />
                </div>
            </footer>
        </div>
    )
}
